import { useMemo, VFC } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';

import HandlebarFormControl from 'dashboard/components/HandlebarFormControl';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { generateJsonSchemaConfig, UsableObjectConfig } from 'dashboard/lib/workflows';
import { WorkflowError } from 'dashboard/models/workflow';
import { WorkflowActionMxGetMicrodepositStatusV1 } from 'dashboard/models/workflow-version';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import useExternalIntegration from 'lib/hooks/useExternalIntegration';
import { ApiModel } from 'lib/interfaces/api';
import { JsonSchemaTypes } from 'lib/json-schema';

interface ParamsMxGetMicrodepositStatusV1Props {
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionMxGetMicrodepositStatusV1['params'];
  usableObjects: UsableObjectConfig[];
  updateActionParams: (newParams: WorkflowActionMxGetMicrodepositStatusV1['params']) => void;
}

const ParamsMxGetMicrodepositStatusV1: VFC<ParamsMxGetMicrodepositStatusV1Props> = ({
  editable,
  errors,
  params,
  usableObjects,
  updateActionParams,
}) => {
  const { jsonSchema, tokensToDisplayValue } = useMemo(() => {
    return generateJsonSchemaConfig({
      usableObjects,
    });
  }, [usableObjects]);

  const updateMxGetMicrodepositStatusParams = <
    Key extends keyof WorkflowActionMxGetMicrodepositStatusV1['params'],
  >(
    key: Key,
    value: WorkflowActionMxGetMicrodepositStatusV1['params'][Key],
  ) => {
    updateActionParams({
      ...params,
      [key]: value,
    });
  };

  const [mxApiKeys, { isLoading }] = useExternalIntegration([
    ApiModel.ExternalIntegrationEnrichmentMx,
  ]);

  const mxApiKeysOptions = [
    ...mxApiKeys.map((integration) => ({
      label: integration.attributes.name,
      value: integration.id,
    })),
    { label: 'Add New MX API', value: 'marketplace-managment-redirect' },
  ];

  const navigate = useNavigate();
  const selectMxApiKey = (mxApiKey: string) => {
    if (mxApiKey === 'marketplace-managment-redirect') {
      navigate(`/dashboard/marketplace/mx`);
    } else {
      updateActionParams({
        ...params,
        'external-integration-id': mxApiKey,
      });
    }
  };

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionMxGetMicrodepositStatusV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel
        labelText='MX API Key'
        htmlFor='params-mx-get-microdeposit-status__external-integration-id'
      />
      <Select
        value={params['external-integration-id']}
        onValueChange={selectMxApiKey}
        id='params-mx-get-microdeposit-status__external-integration-id'
        disabled={!editable || isLoading}
        hasError={hasErrorFor('external-integration-id')}
      >
        {mxApiKeysOptions.map((opt) => (
          <Option key={opt.label} value={opt.value}>
            {opt.label}
          </Option>
        ))}
      </Select>
      {renderErrorMessageFor('external-integration-id')}
      <Spacer height={2} />
      <ParamLabel labelText='MX User GUID' htmlFor='params-mx-get-microdeposit-status__user-guid' />
      <HandlebarFormControl
        size='md'
        type='input'
        value={params['user-guid']}
        updateValue={(value: string) => {
          updateMxGetMicrodepositStatusParams('user-guid', value);
        }}
        editable={editable}
        allowedTypes={[JsonSchemaTypes.String]}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        id='params-mx-get-microdeposit-status__user-guid'
        hasError={hasErrorFor('user-guid')}
      />
      {renderErrorMessageFor('user-guid')}
      <Spacer height={2} />
      <ParamLabel
        labelText='MX Micro-Deposit GUID'
        htmlFor='params-mx-get-microdeposit-status__microdeposit-guid'
      />
      <HandlebarFormControl
        size='md'
        type='input'
        value={params['microdeposit-guid']}
        updateValue={(value: string) => {
          updateMxGetMicrodepositStatusParams('microdeposit-guid', value);
        }}
        editable={editable}
        allowedTypes={[JsonSchemaTypes.String]}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        id='params-mx-get-microdeposit-status__microdeposit-guid'
        hasError={hasErrorFor('microdeposit-guid')}
      />
      {renderErrorMessageFor('microdeposit-guid')}
    </>
  );
};
export default ParamsMxGetMicrodepositStatusV1;
